import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Search, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  FileText,
  Shield,
  Scale,
  IndianRupee,
  Phone,
  User
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusSteps = [
  { 
    status: "Report Submitted", 
    icon: FileText, 
    description: "Your complaint has been received" 
  },
  { 
    status: "Documents Verified", 
    icon: CheckCircle, 
    description: "Our team has verified your documents" 
  },
  { 
    status: "FIR Registered", 
    icon: Scale, 
    description: "Police complaint has been filed" 
  },
  { 
    status: "Investigation in Progress", 
    icon: Shield, 
    description: "Case is being investigated" 
  },
  { 
    status: "Recovery Status", 
    icon: IndianRupee, 
    description: "Final resolution of the case" 
  },
];

const CaseTracker = () => {
  const [searchParams] = useSearchParams();
  const [searchType, setSearchType] = useState("caseId");
  const [caseId, setCaseId] = useState("");
  const [phone, setPhone] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [cases, setCases] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const urlCaseId = searchParams.get("caseId");
    if (urlCaseId) {
      setCaseId(urlCaseId);
      searchByCaseId(urlCaseId);
    }
  }, [searchParams]);

  const searchByCaseId = async (id) => {
    const searchId = id || caseId;
    if (!searchId.trim()) {
      toast.error("Please enter a Case ID");
      return;
    }

    setIsLoading(true);
    setError(null);
    setCaseData(null);
    setCases([]);

    try {
      const response = await axios.get(`${API}/cases/${searchId.trim()}`);
      setCaseData(response.data);
    } catch (err) {
      console.error("Search error:", err);
      setError(err.response?.data?.detail || "Case not found. Please check your Case ID.");
    } finally {
      setIsLoading(false);
    }
  };

  const searchByPhone = async () => {
    if (!phone.trim() || !/^\d{10}$/.test(phone)) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    setIsLoading(true);
    setError(null);
    setCaseData(null);
    setCases([]);

    try {
      const response = await axios.get(`${API}/cases/phone/${phone.trim()}`);
      setCases(response.data);
    } catch (err) {
      console.error("Search error:", err);
      setError(err.response?.data?.detail || "No cases found for this phone number.");
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIndex = (status) => {
    const statusMap = {
      "Report Submitted": 0,
      "Documents Verified": 1,
      "FIR Registered": 2,
      "Investigation in Progress": 3,
      "Partial Recovery": 4,
      "Full Recovery": 4,
      "Closed - No Recovery": 4,
    };
    return statusMap[status] ?? 0;
  };

  const getStatusColor = (status) => {
    if (status === "Full Recovery") return "text-[#27ae60]";
    if (status === "Partial Recovery") return "text-[#f39c12]";
    if (status === "Closed - No Recovery") return "text-[#e74c3c]";
    return "text-[#2c3e50]";
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const renderTimeline = (currentStatus) => {
    const currentIndex = getStatusIndex(currentStatus);

    return (
      <div className="relative" data-testid="status-timeline">
        {statusSteps.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isPending = index > currentIndex;
          const StepIcon = step.icon;

          return (
            <div
              key={step.status}
              className={`timeline-step ${isCompleted ? "completed" : ""} ${isCurrent ? "active" : ""}`}
              data-testid={`timeline-step-${index}`}
            >
              <div className={`timeline-dot ${isCompleted ? "completed" : ""} ${isCurrent ? "active" : ""}`}>
                {isCompleted && <CheckCircle className="w-4 h-4 text-white absolute -left-[2px] -top-[2px]" />}
              </div>
              
              <div className="ml-4">
                <div className="flex items-center gap-3 mb-1">
                  <StepIcon className={`w-5 h-5 ${
                    isCompleted ? "text-[#27ae60]" : 
                    isCurrent ? "text-[#2c3e50]" : "text-[#95a5a6]"
                  }`} />
                  <h4 className={`text-lg font-semibold ${
                    isCompleted ? "text-[#27ae60]" : 
                    isCurrent ? "text-[#2c3e50]" : "text-[#95a5a6]"
                  }`}>
                    {index === 4 ? currentStatus : step.status}
                  </h4>
                  {isCurrent && (
                    <span className="bg-[#2c3e50] text-white text-xs font-bold px-2 py-1 rounded-full">
                      CURRENT
                    </span>
                  )}
                </div>
                <p className={`text-base ${
                  isPending ? "text-[#95a5a6]" : "text-[#546e7a]"
                }`}>
                  {step.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCaseDetails = (data) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="shadow-lg border-t-4 border-t-[#2c3e50] mb-6" data-testid="case-details">
        <CardHeader className="pb-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-sm text-[#546e7a] uppercase tracking-wide">Case ID</p>
              <CardTitle className="text-2xl md:text-3xl font-bold text-[#2c3e50] font-['Manrope']">
                {data.case_id}
              </CardTitle>
            </div>
            <div className={`text-lg font-bold px-4 py-2 rounded-lg ${
              data.status === "Full Recovery" ? "bg-green-100 text-[#27ae60]" :
              data.status === "Partial Recovery" ? "bg-amber-100 text-[#f39c12]" :
              data.status === "Closed - No Recovery" ? "bg-red-100 text-[#e74c3c]" :
              "bg-slate-100 text-[#2c3e50]"
            }`} data-testid="case-status-badge">
              {data.status}
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-6 md:p-8">
          {/* Status Timeline */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-[#2c3e50] font-['Manrope'] mb-6">
              Case Progress
            </h3>
            {renderTimeline(data.status)}
          </div>

          {/* Case Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Victim Info */}
            <div className="bg-slate-50 rounded-lg p-6">
              <h4 className="text-lg font-bold text-[#2c3e50] mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Victim Details
              </h4>
              <div className="space-y-2">
                <p className="text-base"><span className="font-semibold">Name:</span> {data.victim.name}</p>
                <p className="text-base"><span className="font-semibold">City:</span> {data.victim.city}, {data.victim.state}</p>
                <p className="text-base"><span className="font-semibold">Phone:</span> {data.victim.phone}</p>
              </div>
            </div>

            {/* Financial Info */}
            <div className="bg-red-50 rounded-lg p-6">
              <h4 className="text-lg font-bold text-[#e74c3c] mb-4 flex items-center gap-2">
                <IndianRupee className="w-5 h-5" />
                Financial Impact
              </h4>
              <div className="space-y-2">
                <p className="text-2xl font-bold text-[#e74c3c]">
                  {formatCurrency(data.financial.amount_lost)}
                </p>
                <p className="text-base"><span className="font-semibold">Bank:</span> {data.financial.bank_name}</p>
                <p className="text-base"><span className="font-semibold">Method:</span> {data.financial.transaction_method}</p>
              </div>
            </div>

            {/* Scam Info */}
            <div className="bg-amber-50 rounded-lg p-6">
              <h4 className="text-lg font-bold text-[#f39c12] mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Scam Details
              </h4>
              <div className="space-y-2">
                <p className="text-base"><span className="font-semibold">Type:</span> {data.scam.scam_type}</p>
                <p className="text-base"><span className="font-semibold">Contact Method:</span> {data.scam.contact_method}</p>
                <p className="text-base"><span className="font-semibold">Date:</span> {formatDate(data.scam.incident_date)}</p>
              </div>
            </div>

            {/* Legal Info */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="text-lg font-bold text-[#3498db] mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5" />
                Legal Status
              </h4>
              <div className="space-y-2">
                <p className="text-base">
                  <span className="font-semibold">Police Complaint:</span>{" "}
                  {data.legal.police_complaint_filed ? "Yes" : "No"}
                </p>
                {data.legal.fir_number && (
                  <p className="text-base"><span className="font-semibold">FIR No:</span> {data.legal.fir_number}</p>
                )}
                {data.legal.police_station && (
                  <p className="text-base"><span className="font-semibold">Police Station:</span> {data.legal.police_station}</p>
                )}
                <p className="text-base"><span className="font-semibold">Agency:</span> {data.legal.investigating_agency}</p>
              </div>
            </div>
          </div>

          {/* Status History */}
          {data.status_history && data.status_history.length > 0 && (
            <div className="mt-8">
              <h3 className="text-xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
                Status History
              </h3>
              <div className="bg-slate-50 rounded-lg p-4">
                {data.status_history.map((entry, index) => (
                  <div key={index} className="flex items-start gap-4 py-3 border-b border-slate-200 last:border-0">
                    <Clock className="w-5 h-5 text-[#546e7a] mt-1" />
                    <div>
                      <p className="text-base font-semibold text-[#2c3e50]">{entry.status}</p>
                      <p className="text-sm text-[#546e7a]">{formatDate(entry.timestamp)}</p>
                      {entry.notes && <p className="text-sm text-[#95a5a6] mt-1">{entry.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Report Date */}
          <div className="mt-6 text-center text-[#546e7a]">
            <p className="text-base">
              Report submitted on <strong>{formatDate(data.created_at)}</strong>
            </p>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-8 md:py-12" data-testid="case-tracker-page">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#2c3e50] font-['Manrope'] mb-2">
            Track Your Case
          </h1>
          <p className="text-lg text-[#546e7a]">
            Enter your Case ID or Phone Number to check the status
          </p>
        </div>

        {/* Search Card */}
        <Card className="shadow-lg mb-8" data-testid="search-card">
          <CardContent className="p-6 md:p-8">
            <Tabs value={searchType} onValueChange={setSearchType}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger 
                  value="caseId" 
                  className="text-base py-3"
                  data-testid="tab-case-id"
                >
                  Search by Case ID
                </TabsTrigger>
                <TabsTrigger 
                  value="phone" 
                  className="text-base py-3"
                  data-testid="tab-phone"
                >
                  Search by Phone
                </TabsTrigger>
              </TabsList>

              <TabsContent value="caseId">
                <div className="space-y-4">
                  <div>
                    <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                      Enter Your Case ID
                    </Label>
                    <Input
                      type="text"
                      placeholder="e.g., PEFF-20260110-ABC123"
                      value={caseId}
                      onChange={(e) => setCaseId(e.target.value.toUpperCase())}
                      className="input-senior"
                      data-testid="input-case-id"
                    />
                  </div>
                  <Button
                    onClick={() => searchByCaseId()}
                    disabled={isLoading}
                    className="w-full bg-[#2c3e50] hover:bg-[#1a252f] text-white text-lg font-bold py-4 rounded-lg"
                    data-testid="btn-search-case-id"
                  >
                    <Search className="w-5 h-5 mr-2" />
                    {isLoading ? "Searching..." : "Search Case"}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="phone">
                <div className="space-y-4">
                  <div>
                    <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                      Enter Your Phone Number
                    </Label>
                    <Input
                      type="tel"
                      placeholder="10-digit mobile number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      className="input-senior"
                      data-testid="input-phone-search"
                    />
                  </div>
                  <Button
                    onClick={searchByPhone}
                    disabled={isLoading}
                    className="w-full bg-[#2c3e50] hover:bg-[#1a252f] text-white text-lg font-bold py-4 rounded-lg"
                    data-testid="btn-search-phone"
                  >
                    <Search className="w-5 h-5 mr-2" />
                    {isLoading ? "Searching..." : "Search Cases"}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-red-50 border-red-200 mb-6" data-testid="error-message">
              <CardContent className="p-6 flex items-center gap-4">
                <AlertCircle className="w-8 h-8 text-[#e74c3c]" />
                <div>
                  <h3 className="text-lg font-bold text-[#e74c3c]">Case Not Found</h3>
                  <p className="text-base text-[#e74c3c]">{error}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Single Case Result */}
        {caseData && renderCaseDetails(caseData)}

        {/* Multiple Cases Result */}
        {cases.length > 0 && (
          <div className="space-y-4" data-testid="multiple-cases">
            <h3 className="text-xl font-bold text-[#2c3e50] font-['Manrope']">
              Found {cases.length} case(s)
            </h3>
            {cases.map((c) => (
              <Card 
                key={c.case_id} 
                className="shadow-md cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => {
                  setCaseData(c);
                  setCases([]);
                }}
                data-testid={`case-card-${c.case_id}`}
              >
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <p className="text-lg font-bold text-[#2c3e50]">{c.case_id}</p>
                    <p className="text-base text-[#546e7a]">
                      {c.scam.scam_type} • {formatCurrency(c.financial.amount_lost)}
                    </p>
                  </div>
                  <div className={`text-sm font-bold px-3 py-1 rounded-full ${
                    c.status === "Full Recovery" ? "bg-green-100 text-[#27ae60]" :
                    c.status === "Partial Recovery" ? "bg-amber-100 text-[#f39c12]" :
                    "bg-slate-100 text-[#2c3e50]"
                  }`}>
                    {c.status}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Help Section */}
        <Card className="bg-blue-50 border-blue-200 mt-8" data-testid="help-section">
          <CardContent className="p-6 flex items-start gap-4">
            <Phone className="w-8 h-8 text-[#3498db] flex-shrink-0" />
            <div>
              <h3 className="text-lg font-bold text-[#2c3e50] mb-2">Need Help?</h3>
              <p className="text-base text-[#546e7a]">
                If you have lost your Case ID, please call the National Cyber Crime Helpline at{" "}
                <a href="tel:1930" className="font-bold text-[#e74c3c]">1930</a>{" "}
                or visit{" "}
                <a href="https://cybercrime.gov.in" target="_blank" rel="noopener noreferrer" className="font-bold text-[#3498db] underline">
                  cybercrime.gov.in
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CaseTracker;
